import { Formik, Form, Field } from 'formik';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingIndicator from '../../Common/components/LoadingIndicator';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import useFTPSettings, { FTPGallerySettings } from './hooks/useFTPSettings';

export default function EditFTPSettings() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<FTPGallerySettings | null>(null);
    const { getFTPSettings, saveFTPSettingsWithPassword, listFTPDirectories } =
        useFTPSettings();
    const { galleryId: id } = useParams();
    const galleryId = id ? +id : 0;

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const settings: FTPGallerySettings = await getFTPSettings(
                    galleryId,
                );

                isMounted && setSettings(settings);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading || !settings) {
        return <LoadingIndicator />;
    }

    return (
        <div className="max-w-3xl mx-auto py-8 px-4">
            <div className="flex items-center gap-2 mb-6">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">FTP Sync Settings</h1>
            </div>
            <Formik
                initialValues={settings}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await saveFTPSettingsWithPassword(
                        id ? +id : 0,
                        values,
                    );
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        navigate(-1);
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-2">
                            <Label htmlFor="ftpHost">Host</Label>
                            <Field
                                as={Input}
                                id="ftpHost"
                                name="ftpHost"
                                placeholder="Host Address"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ftpPort">Port</Label>
                            <Field
                                as={Input}
                                id="ftpPort"
                                name="ftpPort"
                                placeholder="Port Number (21, 22, 990)"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ftpUser">Username</Label>
                            <Field
                                as={Input}
                                id="ftpUser"
                                name="ftpUser"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ftpPassword">Password</Label>
                            <Field
                                as={Input}
                                id="ftpPassword"
                                name="ftpPassword"
                                type="password"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ftpDirectory">Directory</Label>
                            <Field
                                as={Input}
                                id="ftpDirectory"
                                name="ftpDirectory"
                            />
                        </div>

                        <div className="flex items-center gap-3 pt-6 border-t border-border">
                            <Button type="submit">
                                Save Settings
                            </Button>
                            <Button
                                type="button"
                                onClick={() => navigate(-1)}
                                variant="outline"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={() => listFTPDirectories(galleryId)}
                                variant="outline"
                            >
                                Test
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
